import type { Column, Table } from '@tanstack/react-table';
import { SlidersHorizontal, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DataTableDateFilter } from '@/components/data-table/data-table-date-filter';
import { DataTableFacetedFilter } from '@/components/data-table/data-table-faceted-filter';
import { DataTableSliderFilter } from '@/components/data-table/data-table-slider-filter';
import { DataTableNumberFilter } from '@/components/data-table/data-table-number-filter';
import { DataTableMoneyFilter } from '@/components/data-table/data-table-money-filter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from "@/lib/utils";
import { DataTableAdvancedToolbar } from './data-table-advanced-toolbar';
import { DataTableAsyncFacetedFilter } from './data-table-async-faceted-filter';

import { DebouncedFilterInput } from '@/components/data-table/debounced-filter-input';

interface DataTableToolbarProps<TData> extends React.ComponentProps<'div'> {
  table: Table<TData>;
  isLoading?: boolean;
  onRefresh?: () => void;
  onImport?: () => void;
  onExport?: () => void;
  showFilters?: boolean;
  extraFilters?: React.ReactNode;
}

export function DataTableToolbar<TData>({
  table,
  isLoading,
  children,
  className,
  onRefresh,
  onImport,
  onExport,
  showFilters = true,
  extraFilters,
  ...props
}: DataTableToolbarProps<TData>) {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const [filterSheetOpen, setFilterSheetOpen] = React.useState(false);
  const isFiltered = table.getState().columnFilters.length > 0;
  const activeFiltersCount = table.getState().columnFilters.length;

  const columns = React.useMemo(
    () => table.getAllColumns().filter((column) => column.getCanFilter()),
    [table],
  );

  const onReset = React.useCallback(() => {
    table.resetColumnFilters();
  }, [table]);

  return (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      className={cn('flex w-full flex-col items-start justify-end gap-2 p-1 lg:flex-row', className)}
      {...props}
    >
      {/* Desktop: inline filters */}
      {showFilters && (
        <div className="hidden flex-1 flex-wrap items-center gap-2 md:flex">
          {columns.map((column) => (
            <DataTableToolbarFilter key={column.id} column={column} />
          ))}
          {extraFilters}
          {isFiltered && (
            <Button
              aria-label={t('resetFilters')}
              variant="outline"
              size="sm"
              className="border-dashed"
              onClick={onReset}
            >
              <X />
              {t('reset')}
            </Button>
          )}
        </div>
      )}

      {/* Mobile: filter sheet trigger */}
      {showFilters && isMobile && columns.length > 0 && (
        <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 w-full justify-between md:hidden">
              <span className="inline-flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                {t('filters')}
              </span>
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-[10px]">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
            <SheetHeader className="border-b">
              <SheetTitle>{t('filters')}</SheetTitle>
              <SheetDescription className="sr-only">
                {t('adjustFilters')}
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-col gap-3">
                {columns.map((column) => (
                  <div key={column.id} className="flex flex-col gap-1.5">
                    <DataTableToolbarFilter column={column} fullWidth />
                  </div>
                ))}
                {extraFilters}
              </div>
            </div>
            <SheetFooter className="border-t">
              {isFiltered && (
                <Button variant="outline" onClick={onReset} className="w-full">
                  <X className="mr-2 h-4 w-4" />
                  {t('resetFilters')}
                </Button>
              )}
              <Button onClick={() => setFilterSheetOpen(false)} className="w-full">
                {t('apply')}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      )}

      <div className="flex w-full flex-wrap items-start gap-2 md:w-auto md:flex-none md:flex-nowrap">
        {children}
        <DataTableAdvancedToolbar
          onRefresh={onRefresh}
          table={table}
          isLoading={isLoading}
          onImport={onImport}
          onExport={onExport}
        />
      </div>
    </div>
  );
}
interface DataTableToolbarFilterProps<TData> {
  column: Column<TData>;
  fullWidth?: boolean;
}

function DataTableToolbarFilter<TData>({ column, fullWidth }: DataTableToolbarFilterProps<TData>) {
  {
    const columnMeta = column.columnDef.meta;

    const onFilterRender = React.useCallback(() => {
      if (!columnMeta?.variant) return null;

      switch (columnMeta.variant) {
        case 'text':
          return (
            <DebouncedFilterInput
              column={column}
              placeholder={columnMeta.placeholder ?? columnMeta.label}
              className={cn('h-9 sm:h-8', fullWidth ? 'w-full' : columnMeta.filterWidth || 'w-40 lg:w-56')}
            />
          );

        case 'number':
          return <DataTableNumberFilter column={column} title={columnMeta.label ?? column.id} />;

        case 'money':
          return (
            <DataTableMoneyFilter
              column={column}
              title={columnMeta.label ?? column.id}
              currency={columnMeta.currency || 'USD'}
            />
          );

        case 'range':
          return <DataTableSliderFilter column={column} title={columnMeta.label ?? column.id} />;

        case 'date':
        case 'dateRange':
          return (
            <DataTableDateFilter
              column={column}
              title={columnMeta.label ?? column.id}
              multiple={columnMeta.variant === 'dateRange'}
            />
          );

        case 'select':
        case 'multiSelect':
          return (
            <DataTableFacetedFilter
              column={column}
              title={columnMeta.label ?? column.id}
              options={columnMeta.options ?? []}
              multiple={columnMeta.variant === 'multiSelect'}
            />
          );

        case 'multiAsyncSelect':
          return (
            <DataTableAsyncFacetedFilter
              useAsyncHook={columnMeta.asyncFilterOptions}
              column={column}
              title={columnMeta.label ?? column.id}
            />
          );

        default:
          return null;
      }
    }, [column, columnMeta]);

    return onFilterRender();
  }
}
